import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  DELETE_NON_BPS_ATTACHMENT,
  GET_NON_BPS_ATTACHMENT_TYPES,
  GET_NON_BPS_REGISTRATION_STATUS,
  GET_PRODUCT_CATEGORIES,
  SAVE_NON_BPS_COMPANY_SECTION_B,
  SAVE_NON_BPS_SECTION_A,
  SUBMIT_NON_BPS_REGISTRATION,
  UPLOAD_NON_BPS_ATTACHMENT,
  VERIFY_NON_BPS_TIN,
  VIEW_NON_BPS_ATTACHMENT,
} from '../graphql/non-bps-registration.graphql';
import { GET_NON_BPS_OPERATOR_BY_UUID } from '@modules/external-services/non-bps-management/graphql/non-bps-management.graphql';
import {
  GetNonBpsOperatorByUuidQueryResponse,
  LoadNonBpsAttachmentTypesQueryResponse,
  LoadNonBpsProductCategoriesQueryResponse,
  LoadNonBpsProgressQueryResponse,
  SaveNonBpsSectionAMutationResponse,
  SaveNonBpsSectionBMutationResponse,
  SubmitNonBpsRegistrationMutationResponse,
  UploadNonBpsAttachmentMutationResponse,
  VerifyNonBpsTinQueryResponse,
} from '../responses/non-bps-registration.response';
import {
  NonBpsAttachmentTypesActions,
  NonBpsDeleteAttachmentActions,
  NonBpsLoadOperatorByUuidActions,
  NonBpsProductCategoriesActions,
  NonBpsProgressActions,
  NonBpsSectionAActions,
  NonBpsSectionBActions,
  NonBpsSubmitRegistrationActions,
  NonBpsTinVerificationActions,
  NonBpsUploadAttachmentActions,
  NonBpsViewAttachmentActions,
} from './non-bps-registration.action';

@Injectable({
  providedIn: 'root',
})
export class NonBpsRegistrationEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly graphQlService: GraphQlService,
  ) { }

  private isSuccessStatus(status?: boolean | string): boolean {
    return status === true || status === 'SUCCESS';
  }

  readonly loadProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsProgressActions.request),
      switchMap(() =>
        this.graphQlService
          .query<LoadNonBpsProgressQueryResponse>({
            query: GET_NON_BPS_REGISTRATION_STATUS,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getMyNonBpsRegistrationProgress;
              if (!this.isSuccessStatus(payload?.status)) {
                return NonBpsProgressActions.failure({
                  error: { message: payload?.message ?? 'Failed to load Non-BPS progress' },
                });
              }

              return NonBpsProgressActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'Progress loaded',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsProgressActions.failure({
                  error: { message: error?.message ?? 'Failed to load Non-BPS progress' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadAttachmentTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsAttachmentTypesActions.request),
      switchMap(() =>
        this.graphQlService
          .query<LoadNonBpsAttachmentTypesQueryResponse>({
            query: GET_NON_BPS_ATTACHMENT_TYPES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { registrationType: 'NON_BPS_OPERATOR' },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getAttachmentTypesByRegistrationType ?? [];
              return NonBpsAttachmentTypesActions.success({
                response: {
                  success: true,
                  message: 'Attachment types loaded',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsAttachmentTypesActions.failure({
                  error: { message: error?.message ?? 'Failed to load attachment types' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadProductCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsProductCategoriesActions.request),
      switchMap(() =>
        this.graphQlService
          .query<LoadNonBpsProductCategoriesQueryResponse>({
            query: GET_PRODUCT_CATEGORIES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { fetchAll: true },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getProductCategories;
              if (!this.isSuccessStatus(payload?.status)) {
                return NonBpsProductCategoriesActions.failure({
                  error: { message: payload?.message ?? 'Failed to load product categories' },
                });
              }

              return NonBpsProductCategoriesActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'Product categories loaded',
                  statusCode: 200,
                  data: payload?.data ?? [],
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsProductCategoriesActions.failure({
                  error: { message: error?.message ?? 'Failed to load product categories' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveSectionA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSectionAActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SaveNonBpsSectionAMutationResponse>({
            mutation: SAVE_NON_BPS_SECTION_A,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveNonBpsDetails;
              if (!this.isSuccessStatus(payload?.status)) {
                return NonBpsSectionAActions.failure({
                  error: { message: payload?.message ?? 'Failed to save section A' },
                });
              }

              return NonBpsSectionAActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'Section A saved',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsSectionAActions.failure({
                  error: { message: error?.message ?? 'Failed to save section A' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly verifyTin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsTinVerificationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<VerifyNonBpsTinQueryResponse>({
            query: VERIFY_NON_BPS_TIN,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.verifyTaxId;
              const tinData = (payload as any)?.data;
              const hasTaxpayerRecord = Boolean(tinData?.taxpayerName || tinData?.tin);

              const isNoRecord =
                !this.isSuccessStatus(payload?.status) ||
                !hasTaxpayerRecord ||
                (payload as any)?.code === 'NO_RECORD_FOUND';

              if (isNoRecord) {
                const isDeceptiveSuccessMessage = payload?.message?.toLowerCase().includes('success');
                const message =
                  payload?.message && !isDeceptiveSuccessMessage
                    ? payload.message
                    : 'No taxpayer record found for the provided TIN with TRA. Please verify your TIN and try again.';

                return NonBpsTinVerificationActions.failure({
                  error: { message },
                });
              }

              return NonBpsTinVerificationActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'TIN verified successfully',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsTinVerificationActions.failure({
                  error: { message: error?.message ?? 'TIN verification failed' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly saveSectionB$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSectionBActions.request),
      switchMap(() =>
        this.graphQlService
          .mutate<SaveNonBpsSectionBMutationResponse>({
            mutation: SAVE_NON_BPS_COMPANY_SECTION_B,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((result) => {
              const payload = result.data?.saveImportationLicenseDetails;
              if (!this.isSuccessStatus(payload?.status)) {
                return NonBpsSectionBActions.failure({
                  error: { message: payload?.message ?? 'Failed to save section B' },
                });
              }

              return NonBpsSectionBActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'Section B saved',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsSectionBActions.failure({
                  error: { message: error?.message ?? 'Failed to save section B' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly uploadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsUploadAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<UploadNonBpsAttachmentMutationResponse>({
            mutation: UPLOAD_NON_BPS_ATTACHMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: {
                attachableUuid: request.attachableUuid,
                registrationAttachmentTypeUuid: request.registrationAttachmentTypeUuid,
                fileDto: {
                  filename: request.filename,
                  extension: request.extension,
                  data: request.data,
                },
              },
            },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.uploadAttachment;
              if (!this.isSuccessStatus(payload?.status)) {
                return NonBpsUploadAttachmentActions.failure({
                  error: { message: payload?.message ?? 'Attachment upload failed' },
                });
              }

              return NonBpsUploadAttachmentActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'Attachment uploaded',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsUploadAttachmentActions.failure({
                  error: { message: error?.message ?? 'Attachment upload failed' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly submitRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsSubmitRegistrationActions.request),
      switchMap(() =>
        this.graphQlService
          .mutate<SubmitNonBpsRegistrationMutationResponse>({
            mutation: SUBMIT_NON_BPS_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((result) => {
              const payload = result.data?.submitNonBpsRegistration;
              if (!this.isSuccessStatus(payload?.status)) {
                return NonBpsSubmitRegistrationActions.failure({
                  error: { message: payload?.message ?? 'Failed to submit Non-BPS registration' },
                });
              }

              return NonBpsSubmitRegistrationActions.success({
                response: {
                  success: true,
                  message: payload?.message ?? 'Non-BPS registration submitted',
                  statusCode: 200,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsSubmitRegistrationActions.failure({
                  error: { message: error?.message ?? 'Failed to submit Non-BPS registration' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly loadOperatorByUuid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsLoadOperatorByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetNonBpsOperatorByUuidQueryResponse>({
            query: GET_NON_BPS_OPERATOR_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.getNonBpsOperatorByUuid;
              if (!payload) {
                return NonBpsLoadOperatorByUuidActions.failure({
                  error: { message: 'No data returned' },
                });
              }
              return NonBpsLoadOperatorByUuidActions.success({
                response: {
                  success: this.isSuccessStatus(payload.status),
                  message: payload.message ?? 'Operator loaded',
                  statusCode: 200,
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsLoadOperatorByUuidActions.failure({
                  error: { message: error?.message ?? 'Failed to load operator' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly viewAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsViewAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<{ viewAttachmentByUuid: { code: string; status: boolean; message: string; data: string } }>({
            query: VIEW_NON_BPS_ATTACHMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { attachmentUuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.viewAttachmentByUuid;
              if (!payload?.status) {
                return NonBpsViewAttachmentActions.failure({
                  error: { message: payload?.message ?? 'Failed to view attachment' },
                });
              }
              return NonBpsViewAttachmentActions.success({
                response: {
                  success: true,
                  message: payload.message ?? 'Attachment loaded',
                  statusCode: 200,
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsViewAttachmentActions.failure({
                  error: { message: error?.message ?? 'Failed to view attachment' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly deleteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NonBpsDeleteAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<{ deleteAttachment: { code: string; status: boolean; message: string; data: any } }>({
            mutation: DELETE_NON_BPS_ATTACHMENT,
            apolloNamespace: ApolloNamespace.registration,
            variables: { attachmentUuid: request },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.deleteAttachment;
              if (!payload?.status) {
                return NonBpsDeleteAttachmentActions.failure({
                  error: { message: payload?.message ?? 'Failed to delete attachment' },
                });
              }
              return NonBpsDeleteAttachmentActions.success({
                response: {
                  success: true,
                  message: payload.message ?? 'Attachment deleted',
                  statusCode: 200,
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                NonBpsDeleteAttachmentActions.failure({
                  error: { message: error?.message ?? 'Failed to delete attachment' },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
